import { useCallback, useEffect, useState } from "react";
import {
  fetchSocialListeningRuns,
  type SocialListeningRun,
} from "src/services/SocialListeningAPIs";
import { getStoredId, POLL_INTERVAL_MS } from "../Utils/SocialListeningConstants";

/** The tenant's runs, newest first, refreshed while any of them is still pending. */
export const useSocialListeningHistory = () => {
  const [runs, setRuns] = useState<SocialListeningRun[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [requestKey, setRequestKey] = useState(0);
  const hasPendingRun = runs.some(run => run.status === "pending");

  useEffect(() => {
    const userId = getStoredId("user_id");
    if (userId === null) {
      setIsLoading(false);
      setHasError(true);
      return undefined;
    }

    let isCancelled = false;
    fetchSocialListeningRuns(userId)
      .then(data => {
        if (!isCancelled) {
          setRuns(data);
          setHasError(false);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setHasError(true);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [requestKey]);

  useEffect(() => {
    if (!hasPendingRun) {
      return undefined;
    }
    const timer = window.setTimeout(() => setRequestKey(key => key + 1), POLL_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [hasPendingRun, runs]);

  const reload = useCallback(() => setRequestKey(key => key + 1), []);

  return { runs, setRuns, isLoading, hasError, reload };
};
